import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../../lib/axios';
import {
    TextField, Grid, Paper, Box, Typography, Autocomplete, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button
} from '@mui/material';
import { selectGlobalProperty } from '../../../redux/propertySlice';
import { useSelector } from 'react-redux';
import debounce from 'lodash.debounce';
import { useNavigate } from 'react-router-dom';
import { ListItem, ListItemAvatar, Avatar, ListItemText } from '@mui/material';

const EnqList = ({ setOpenEnq }) => {
    const [suggestions, setSuggestions] = useState([]);
    const [selectedEnquiries, setSelectedEnquiries] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [openNoRecordDialog, setOpenNoRecordDialog] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(false);
    const globalProperty = useSelector(selectGlobalProperty);
    const navigate = useNavigate();

    useEffect(() => {
        if (inputValue === '') {
            setSelectedEnquiries([]);
            setSuggestions([]);
        }
    }, [inputValue]);

    // Fetch suggestions while typing
    const debouncedFetch = useCallback(
        debounce(async (value) => {
            if (!value || value.length < 3) {
                setSuggestions([]);
                setOpenDropdown(false);
                return;
            }
            try {
                const response = await axiosInstance.post("TrnEnq/GetTrnEnqDashBoard?currentPage=1&limit=15", {
                    PageNumber: 1,
                    PageSize: 10,
                    SearchText: value,
                    PropId: globalProperty,
                    Flag: "Open",
                    FlwUserId: 9
                });

                if (response.data.Status === 0) {
                    const data = response.data.Data || [];
                    setSuggestions(data);
                    if (data.length > 0) {
                        setOpenDropdown(true);
                    } else {
                        setOpenDropdown(false);
                        setOpenNoRecordDialog(true);
                    }
                } else {
                    setSuggestions([]);
                    setOpenDropdown(false);
                    setOpenNoRecordDialog(true);
                }
            } catch (error) {
                toast.error("Error while fetching suggestions.");
                setSuggestions([]);
                setOpenDropdown(false);
            }
        }, 600),
        [globalProperty]
    );

    useEffect(() => {
        debouncedFetch(inputValue);
        return () => debouncedFetch.cancel();
    }, [inputValue, debouncedFetch]);

    const handleSelect = (event, newValue) => {
        if (newValue) {
            if (typeof newValue === 'string') {
                setSelectedEnquiries([]);
            } else {
                setSelectedEnquiries([newValue]);
            }
            setSuggestions([]);
        }
    };

    // Dialog handlers
    const handleYes = () => {
        setOpenNoRecordDialog(false);
        navigate('/enquiry');
    };

    const handleNo = () => {
        setOpenNoRecordDialog(false);
    };

    const handleDoubleClick = (row) => {
        if (row?.EnqId) {
            navigate('/visitor', { state: { EnqId: row.EnqId } });
        }
        setOpenEnq(false);
    };

    return (
        <Grid container spacing={2} className='form_field'>
            <Grid item xs={12} sm={12}>
                <Autocomplete
                    freeSolo
                    open={openDropdown}
                    onOpen={() => suggestions.length > 0 && setOpenDropdown(true)}
                    onClose={() => setOpenDropdown(false)}
                    options={suggestions}
                    getOptionLabel={(option) => typeof option === 'string' ? option : `${option.EnqBy} - ${option.EnqNo}`}
                    filterOptions={(options) => options}
                    inputValue={inputValue}
                    onInputChange={(event, newInputValue, reason) => {
                        if (reason !== 'reset') {
                            setInputValue(newInputValue);
                        }
                    }}
                    onChange={(event, newValue) => {
                        if (newValue) {
                            if (typeof newValue === 'string') {
                                setSelectedEnquiries([]);
                                setInputValue(newValue);
                            } else {
                                setSelectedEnquiries([newValue]);
                                setInputValue(newValue.EnqBy);
                            }
                            setSuggestions([]);
                            setOpenDropdown(false);
                        }
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Enter Name/Mobile No"
                            placeholder='Search By Name/Mobile/EnqNo'
                            variant="filled"
                            fullWidth
                            className='custom-textfield'
                        />
                    )}
                    renderOption={(props, option) => (
                        <ListItem {...props} sx={{ cursor: 'pointer', py: 1 }}>
                            <ListItemAvatar>
                                <Avatar sx={{ bgcolor: '#635bff', color: '#fff' }}>
                                    {option.EnqBy ? option.EnqBy.charAt(0).toUpperCase() : '?'}
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body1" fontWeight="bold">
                                            {option.EnqBy}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {option.EnqNo}
                                        </Typography>
                                    </Box>
                                }
                                secondary={
                                    <Typography variant="body2" color="textSecondary">
                                        {option.MobileNo}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    )}
                />
            </Grid>

            {/* Results Cards */}
            <Grid item xs={12}>
                {selectedEnquiries.length > 0 ? (
                    <Grid container spacing={2}>
                        {selectedEnquiries.map((row, index) => (
                            <Grid item xs={12} key={index}>
                                <Paper elevation={3} sx={{ padding: 3, cursor: 'pointer' }} onDoubleClick={() => handleDoubleClick(row)}>
                                    <Box display="flex" flexWrap="wrap" gap={4}>
                                        <Typography variant="body1">
                                            <strong>Enq No:</strong> {row.EnqNo}
                                        </Typography>
                                        <Typography variant="body1">
                                            <strong>Enq By:</strong> {row.EnqBy}
                                        </Typography>
                                    </Box>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 4 }}>
                        Search and select an enquiry to view details
                    </Typography>
                )}
            </Grid>

            {/* No Record Found Dialog */}
            <Dialog
                open={openNoRecordDialog}
                onClose={handleNo}
                aria-labelledby="no-record-dialog-title"
            >
                <DialogTitle id="no-record-dialog-title">
                    No Enquiry Found
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        No enquiry found. Would you like to create a new enquiry?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button size='small' onClick={handleNo} sx={{ bgcolor: '#a83027', ":hover": { bgcolor: '#a83027' } }} variant='contained'>
                        No
                    </Button>
                    <Button size='small' onClick={handleYes} sx={{ bgcolor: '#288625', ":hover": { bgcolor: '#288625' } }} variant="contained">
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default EnqList;